import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useActiveContextReady } from './useActiveContextReady';
import { config } from '../config';

export interface Team {
  id: string;
  name: string;
  organizationId: string;
  divisionId?: string;
  divisionName?: string;
  description?: string;
  isImmutable: boolean; // SSO groups are immutable
  createdAt?: string;
  updatedAt?: string;
}

interface TeamsListResponse {
  teams: Team[];
}

const backendApi = config.backendApi;

/**
 * Formats a team name with its division distinguisher.
 * - If the team has a division, appends " (DivisionName)"
 * - If the team is org-level (no division), appends " (org)"
 */
export function formatTeamName(team: { name: string; divisionName?: string | null }): string {
  if (team.divisionName) {
    return `${team.name} (${team.divisionName})`;
  }
  return `${team.name} (org)`;
}

const fetchTeams = async (): Promise<Team[]> => {
  const response = await axios.get<TeamsListResponse>(`${backendApi}/api/teams`, { withCredentials: true });
  return response.data.teams;
};

export function useTeams(organizationId: string | null | undefined) {
  const contextReady = useActiveContextReady();

  return useQuery<Team[]>({
    queryKey: ['teams', organizationId],
    queryFn: () => {
      if (!organizationId) {
        throw new Error("organizationId is required to fetch teams.");
      }
      return fetchTeams();
    },
    enabled: !!organizationId && contextReady,
  });
}
